import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { FaUpload, FaImage, FaPalette, FaRuler, FaMagic, FaTimes } from 'react-icons/fa';

const AdvancedToolModal = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    selectedCard 
}) => {
    const [formData, setFormData] = useState({
        prompt: '',
        backgroundImage: null,
        backgroundDescription: '',
        size: 'square',
        style: 'realistic',
        colors: '',
        additionalDetails: ''
    });

    // Size options
    const sizeOptions = [
        { value: 'square', label: 'Square (1:1)', icon: '⬜' },
        { value: 'vertical', label: 'Vertical (4:5)', icon: '⬜' },
        { value: 'horizontal', label: 'Horizontal (16:9)', icon: '⬜' },
        { value: 'story', label: 'Story (9:16)', icon: '⬜' },
        { value: 'banner', label: 'Banner (3:1)', icon: '⬜' }
    ];

    // Style options
    const styleOptions = [
        { value: 'realistic', label: 'Realistic', icon: '🎨' },
        { value: 'futuristic', label: 'Futuristic', icon: '🚀' },
        { value: 'cartoon', label: 'Cartoon', icon: '📺' },
        { value: 'sticker', label: 'Sticker', icon: '🏷️' },
        { value: '3d', label: '3D', icon: '🎲' },
        { value: 'anime', label: 'Anime', icon: '🌸' },
        { value: 'watercolor', label: 'Watercolor', icon: '🎨' },
        { value: 'minimalist', label: 'Minimalist', icon: '⚪' },
        { value: 'vintage', label: 'Vintage', icon: '📷' },
        { value: 'cyberpunk', label: 'Cyberpunk', icon: '🌃' }
    ];

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                backgroundImage: file
            }));
        }
    };

    const handleConfirm = () => {
        // Build the enhanced prompt based on form data
        let enhancedPrompt = '';
        
        // Start with specific tool-based instructions
        switch (selectedCard.title) {
            case 'Product Generator':
                enhancedPrompt += 'Generate an image of a product ';
                break;
            case 'Quote Generator':
                enhancedPrompt += 'Create a quote image with text ';
                break;
            case 'Graphic Generator':
                enhancedPrompt += 'Generate a social media graphic ';
                break;
            case 'Social Media Post':
                enhancedPrompt += 'Design a social media post ';
                break;
            case 'Analytics Visualizer':
                enhancedPrompt += 'Create an analytics visualization ';
                break;
            case 'Marketing Campaign':
                enhancedPrompt += 'Generate marketing campaign materials ';
                break;
            case 'Event Promoter':
                enhancedPrompt += 'Design event promotional materials ';
                break;
            case 'Team Collaboration':
                enhancedPrompt += 'Create team collaboration graphics ';
                break;
            case 'Achievement Showcase':
                enhancedPrompt += 'Generate achievement showcase graphics ';
                break;
            default:
                enhancedPrompt += 'Generate an image ';
        }
        
        // Add user's custom description or default prompt
        if (formData.prompt) {
            enhancedPrompt += formData.prompt;
        } else {
            // Remove the generic prompt and use more specific descriptions
            switch (selectedCard.title) {
                case 'Product Generator':
                    enhancedPrompt += 'showcasing a professional product with branding';
                    break;
                case 'Quote Generator':
                    enhancedPrompt += 'featuring inspirational text with beautiful typography';
                    break;
                case 'Graphic Generator':
                    enhancedPrompt += 'suitable for all social media platforms';
                    break;
                case 'Social Media Post':
                    enhancedPrompt += 'optimized for Instagram, Facebook, and Twitter';
                    break;
                case 'Analytics Visualizer':
                    enhancedPrompt += 'displaying data in chart and infographic format';
                    break;
                case 'Marketing Campaign':
                    enhancedPrompt += 'including visuals and copy for campaigns';
                    break;
                case 'Event Promoter':
                    enhancedPrompt += 'for events, posters, and promotional materials';
                    break;
                case 'Team Collaboration':
                    enhancedPrompt += 'showing team photos and organizational charts';
                    break;
                case 'Achievement Showcase':
                    enhancedPrompt += 'for certificates, awards, and achievements';
                    break;
                default:
                    enhancedPrompt += 'with professional design elements';
            }
        }
        
        // Add size specification
        enhancedPrompt += ` in ${formData.size} format`;
        
        // Add style specification
        enhancedPrompt += ` with ${formData.style} style`;
        
        // Add background description
        if (formData.backgroundDescription) {
            enhancedPrompt += ` on ${formData.backgroundDescription} background`;
        }
        
        // Add color specification
        if (formData.colors) {
            enhancedPrompt += ` using ${formData.colors} color palette`;
        }
        
        // Add additional details
        if (formData.additionalDetails) {
            enhancedPrompt += `. ${formData.additionalDetails}`;
        }

        onConfirm(enhancedPrompt);
        onClose();
        // Reset form
        setFormData({
            prompt: '',
            backgroundImage: null,
            backgroundDescription: '',
            size: 'square',
            style: 'realistic',
            colors: '',
            additionalDetails: ''
        });
    };

    const renderFields = () => {
        if (!selectedCard) return null;

        const fields = [];

        // Common fields for all tools
        fields.push(
            <div key="prompt" className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaMagic className="inline mr-2" />
                    Describe what you want to create
                </label>
                <textarea
                    value={formData.prompt}
                    onChange={(e) => handleInputChange('prompt', e.target.value)}
                    placeholder="Describe your vision in detail..."
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                />
            </div>
        );

        // Background image upload (for most tools)
        if (['Product Generator', 'Graphic Generator', 'Social Media Post'].includes(selectedCard.title)) {
            fields.push(
                <div key="background" className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FaImage className="inline mr-2" />
                        Background Image Reference
                    </label>
                    <div className="flex items-center gap-3">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="background-upload"
                        />
                        <label
                            htmlFor="background-upload"
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50"
                        >
                            <FaUpload />
                            Upload Image
                        </label>
                        {formData.backgroundImage && (
                            <span className="text-sm text-green-600">
                                ✓ {formData.backgroundImage.name}
                            </span>
                        )}
                    </div>
                </div>
            );
        }

        // Background description
        fields.push(
            <div key="background-desc" className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Description
                </label>
                <input
                    type="text"
                    value={formData.backgroundDescription}
                    onChange={(e) => handleInputChange('backgroundDescription', e.target.value)}
                    placeholder="e.g., gradient, solid color, nature scene, urban setting..."
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
            </div>
        );

        // Size selection
        fields.push(
            <div key="size" className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaRuler className="inline mr-2" />
                    Image Size
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {sizeOptions.map((option) => (
                        <label
                            key={option.value}
                            className={`flex items-center gap-2 p-3 border rounded-md cursor-pointer transition-colors ${
                                formData.size === option.value
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            <input
                                type="radio"
                                name="size"
                                value={option.value}
                                checked={formData.size === option.value}
                                onChange={(e) => handleInputChange('size', e.target.value)}
                                className="sr-only"
                            />
                            <span className="text-lg">{option.icon}</span>
                            <span className="text-sm">{option.label}</span>
                        </label>
                    ))}
                </div>
            </div>
        );

        // Style selection
        fields.push(
            <div key="style" className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaPalette className="inline mr-2" />
                    Visual Style
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {styleOptions.map((option) => (
                        <label
                            key={option.value}
                            className={`flex items-center gap-2 p-3 border rounded-md cursor-pointer transition-colors ${
                                formData.style === option.value
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            <input
                                type="radio"
                                name="style"
                                value={option.value}
                                checked={formData.style === option.value}
                                onChange={(e) => handleInputChange('style', e.target.value)}
                                className="sr-only"
                            />
                            <span className="text-lg">{option.icon}</span>
                            <span className="text-sm">{option.label}</span>
                        </label>
                    ))}
                </div>
            </div>
        );

        // Color specification
        fields.push(
            <div key="colors" className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color Palette
                </label>
                <input
                    type="text"
                    value={formData.colors}
                    onChange={(e) => handleInputChange('colors', e.target.value)}
                    placeholder="e.g., blue and white, warm tones, monochrome..."
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
            </div>
        );

        // Additional details
        fields.push(
            <div key="additional" className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Details
                </label>
                <textarea
                    value={formData.additionalDetails}
                    onChange={(e) => handleInputChange('additionalDetails', e.target.value)}
                    placeholder="Any specific details, mood, lighting, composition preferences..."
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={2}
                />
            </div>
        );

        return fields;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 modal-overlay">
            <div className="bg-black bg-opacity-50 absolute inset-0" onClick={onClose}></div>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4 relative">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        {selectedCard?.icon}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900">
                                {selectedCard?.title}
                            </h3>
                            <p className="text-sm text-gray-600">
                                {selectedCard?.description}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <FaTimes className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {renderFields()}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleConfirm}
                        disabled={!formData.prompt.trim()}
                    >
                        Generate
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AdvancedToolModal; 